import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

/*
  -------------------------------------------------------------------
  Hook de acessibilidade
  -------------------------------------------------------------------
  Centraliza o alto contraste e o ajuste de tamanho de fonte do
  portal, persistindo as preferências em localStorage. É SSR-safe:
  o estado inicial é determinístico e a reidratação acontece apenas
  no useEffect (cliente), evitando divergência de hydration.
  -------------------------------------------------------------------
*/

const CONTRAST_KEY = 'mpf-a11y-contrast';
const FONT_KEY = 'mpf-a11y-font';
const CONTRAST_CLASS = 'mpf-contrast';

// Passo de fonte: base 16px, +/- 2px por nível, limitado a [-2, +3].
const BASE_FONT_SIZE = 16;
const FONT_STEP_PX = 2;
const MIN_STEP = -2;
const MAX_STEP = 3;

const isClient = () =>
  typeof document !== 'undefined' && typeof window !== 'undefined';

const clamp = (value) => Math.min(MAX_STEP, Math.max(MIN_STEP, value));

const prefersReducedMotion = () =>
  isClient() &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const applyContrast = (enabled) => {
  if (!isClient()) return;
  document.documentElement.classList.toggle(CONTRAST_CLASS, enabled);
};

const applyFontStep = (step) => {
  if (!isClient()) return;
  const size = BASE_FONT_SIZE + step * FONT_STEP_PX;
  document.documentElement.style.setProperty('--base-font-size', `${size}px`);
};

const useAccessibility = () => {
  const [contrast, setContrast] = useState(false);
  const [fontStep, setFontStep] = useState(0);

  // Reidrata as preferências salvas e as reaplica ao DOM.
  useEffect(() => {
    if (!isClient()) return;

    try {
      const savedContrast = window.localStorage.getItem(CONTRAST_KEY) === '1';
      const savedStep = clamp(
        parseInt(window.localStorage.getItem(FONT_KEY) ?? '0', 10) || 0,
      );

      setContrast(savedContrast);
      setFontStep(savedStep);
      applyContrast(savedContrast);
      applyFontStep(savedStep);
    } catch {
      // localStorage indisponível (modo privado/SSR) — ignora.
    }
  }, []);

  const persist = useCallback((key, value) => {
    if (!isClient()) return;
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // ignora falhas de escrita.
    }
  }, []);

  const toggleContrast = useCallback(() => {
    const applyToggle = () => {
      setContrast((prev) => {
        const next = !prev;
        applyContrast(next);
        persist(CONTRAST_KEY, next ? '1' : '0');
        return next;
      });
    };

    /* Sem a View Transitions API (ou sob movimento reduzido), a troca e seca —
       o comportamento de antes da animacao. A animacao em si (mascara circular
       que cresce do centro da tela) mora em `global/_theme-transition.scss`. */
    if (
      !isClient() ||
      typeof document.startViewTransition !== 'function' ||
      prefersReducedMotion()
    ) {
      applyToggle();
      return;
    }

    /* `flushSync` pelo mesmo motivo de `usePrintTimestamp.ts`: sem ele o
       batching do React 18 pode adiar o commit para depois de o navegador
       capturar o estado "depois", e a transicao sai sem efeito. */
    document.startViewTransition(() => flushSync(applyToggle));
  }, [persist]);

  const setStep = useCallback(
    (updater) => {
      setFontStep((prev) => {
        const next = clamp(updater(prev));
        applyFontStep(next);
        persist(FONT_KEY, String(next));
        return next;
      });
    },
    [persist],
  );

  const increaseFont = useCallback(() => setStep((s) => s + 1), [setStep]);
  const decreaseFont = useCallback(() => setStep((s) => s - 1), [setStep]);
  const resetFont = useCallback(() => setStep(() => 0), [setStep]);

  return {
    contrast,
    fontStep,
    toggleContrast,
    increaseFont,
    decreaseFont,
    resetFont,
  };
};

export default useAccessibility;
